import { useFormContext } from 'react-hook-form';
import { Button } from '@tremor/react';
import type { z as zod } from 'zod';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { TextInput, Textarea, PriceInput, MultiSelect } from '@/components';
import { type SustainabilityAction, type Audience } from '@/api/model';
import type { grantSchema } from '@/schemas';

export function GrantForm({
  sustainabilityActions,
  audiences,
  onSave,
}: {
  readonly sustainabilityActions: readonly SustainabilityAction[];
  readonly audiences: readonly Audience[];
  readonly onSave: () => void;
}) {
  const { _ } = useLingui();
  const {
    control,
    formState: { errors },
  } = useFormContext<zod.infer<typeof grantSchema>>();

  return (
    <div className='flex flex-col gap-4'>
      <TextInput
        name='title'
        label={_(msg`Title`)}
        placeholder={_(msg`Title`)}
        control={control}
        errorMessage={errors.title?.message}
      />
      <Textarea
        name='description'
        label={_(msg`Description`)}
        placeholder={_(msg`Description`)}
        control={control}
        errorMessage={errors.description?.message}
      />
      <TextInput
        name='deadline'
        label={_(msg`Deadline`)}
        placeholder={_(msg`Deadline`)}
        control={control}
        errorMessage={errors.deadline?.message}
      />
      <PriceInput
        name='amount'
        label={_(msg`Amount`)}
        placeholder={_(msg`Amount`)}
        control={control}
        errorMessage={errors.amount?.message}
      />
      <TextInput
        name='url'
        label={_(msg`Link`)}
        placeholder={_(msg`Link`)}
        control={control}
        errorMessage={errors.url?.message}
        type='url'
      />
      <MultiSelect
        name='sustainabilityActionIds'
        label={_(msg`Sustainability actions`)}
        options={sustainabilityActions.map((sustainabilityAction) => ({
          value: sustainabilityAction.id.toString(),
          label: sustainabilityAction.title,
        }))}
        control={control}
        errorMessage={errors.sustainabilityActionIds?.message}
      />
      <MultiSelect
        name='audienceIds'
        label={_(msg`Audiences`)}
        options={audiences.map((audience) => ({
          value: audience.id.toString(),
          label: audience.name,
        }))}
        control={control}
        errorMessage={errors.audienceIds?.message}
      />
      <div className='flex justify-end mb-4'>
        <Button onClick={onSave}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  );
}
